/**
 * Client-side expansion of metrics date-range preset tokens (e.g.
 * `week_to_date`, `last_12_weeks`) into concrete `YYYY-MM-DD` bounds, so Home
 * "View all" links can carry a metric tile's window as absolute bookings-list
 * date filters. Dates are emitted in the browser's local timezone to match the
 * bookings list date filters.
 *
 * Keep in sync with the Rust resolver `metrics_filters::resolve_date_range`.
 */
import { currentWeekBounds, toLocalIsoDate } from "./dateHorizon";

/** Resolved `[from, to]` window; either bound may be absent (open-ended). */
export interface DateRangeBounds {
  from?: string;
  to?: string;
}

function mondayOf(d: Date): Date {
  const dow = d.getDay(); // 0 = Sunday .. 6 = Saturday
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  monday.setDate(monday.getDate() + mondayOffset);
  return monday;
}

function quarterStart(d: Date): Date {
  const qMonth = Math.floor(d.getMonth() / 3) * 3;
  return new Date(d.getFullYear(), qMonth, 1);
}

/**
 * Expand a date-range preset token into local `YYYY-MM-DD` bounds relative to
 * today. Returns `null` for unrecognized tokens, and `{}` (both open) for
 * `all_time`.
 */
export function resolveDateRange(
  token: string,
  now: Date = new Date(),
): DateRangeBounds | null {
  const t = token.trim().toLowerCase();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayIso = toLocalIsoDate(today);

  const lastN = (suffix: string): number | null => {
    const raw = t.startsWith("last_") && t.endsWith(suffix)
      ? t.slice("last_".length, t.length - suffix.length)
      : null;
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };

  const days = lastN("_days");
  if (days !== null) {
    const from = new Date(today);
    from.setDate(from.getDate() - days);
    return { from: toLocalIsoDate(from), to: todayIso };
  }

  const weeks = lastN("_weeks");
  if (weeks !== null) {
    const from = new Date(today);
    from.setDate(from.getDate() - weeks * 7);
    return { from: toLocalIsoDate(from), to: todayIso };
  }

  const months = lastN("_months");
  if (months !== null) {
    const from = new Date(today);
    from.setMonth(from.getMonth() - months);
    return { from: toLocalIsoDate(from), to: todayIso };
  }

  switch (t) {
    case "all_time":
    case "all":
      return {};
    case "today":
      return { from: todayIso, to: todayIso };
    case "yesterday": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: toLocalIsoDate(y), to: toLocalIsoDate(y) };
    }
    case "week_to_date":
      return { from: toLocalIsoDate(mondayOf(today)), to: todayIso };
    case "this_week":
      return currentWeekBounds(today);
    case "month_to_date":
    case "this_month":
      return {
        from: toLocalIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: todayIso,
      };
    case "quarter_to_date":
    case "this_quarter":
      return { from: toLocalIsoDate(quarterStart(today)), to: todayIso };
    case "year_to_date":
    case "this_year":
    case "ytd":
      return { from: toLocalIsoDate(new Date(today.getFullYear(), 0, 1)), to: todayIso };
    case "last_week": {
      const thisMonday = mondayOf(today);
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(lastMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday);
      lastSunday.setDate(lastSunday.getDate() - 1);
      return { from: toLocalIsoDate(lastMonday), to: toLocalIsoDate(lastSunday) };
    }
    case "last_month": {
      const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastEnd = new Date(firstThis);
      lastEnd.setDate(lastEnd.getDate() - 1);
      const lastStart = new Date(lastEnd.getFullYear(), lastEnd.getMonth(), 1);
      return { from: toLocalIsoDate(lastStart), to: toLocalIsoDate(lastEnd) };
    }
    case "last_quarter": {
      const qStart = quarterStart(today);
      const lastQEnd = new Date(qStart);
      lastQEnd.setDate(lastQEnd.getDate() - 1);
      return {
        from: toLocalIsoDate(quarterStart(lastQEnd)),
        to: toLocalIsoDate(lastQEnd),
      };
    }
    case "last_year":
      return {
        from: toLocalIsoDate(new Date(today.getFullYear() - 1, 0, 1)),
        to: toLocalIsoDate(new Date(today.getFullYear() - 1, 11, 31)),
      };
    default:
      return null;
  }
}
